export const QUERY_JOB_TERMINAL_STATUSES = new Set([
  "completed",
  "partial_success",
  "cancelled",
  "failed",
  "error",
]);

export function createQueryDeliveryState() {
  return {
    streamPhase: "connecting",
    hasStreamMessage: false,
    postMode: null,
  };
}

export function advanceQueryDeliveryState(state, event = {}) {
  const current = state || createQueryDeliveryState();

  switch (event.type) {
    case "stream_connected":
      return { ...current, streamPhase: "active" };
    case "stream_message":
      return {
        ...current,
        streamPhase: "result_received",
        hasStreamMessage: true,
      };
    case "stream_terminal":
      return { ...current, streamPhase: "terminal" };
    case "stream_failed":
      return { ...current, streamPhase: "failed" };
    case "post_settled":
      return { ...current, postMode: event.mode || "final" };
    default:
      return current;
  }
}

export function shouldRenderPostResult(state) {
  return state?.postMode === "final" && state?.hasStreamMessage !== true;
}

export function shouldRenderStreamFailure(state) {
  return (
    state?.postMode === "accepted" &&
    state?.streamPhase === "failed" &&
    state?.hasStreamMessage !== true
  );
}

export function isQueryDeliverySettled(state) {
  return Boolean(
    state?.postMode &&
      (state.streamPhase === "terminal" || state.streamPhase === "failed")
  );
}

export function appendAssistantMessageIfMissing(messages = [], incomingMessage) {
  if (!incomingMessage) return messages;

  const responseId = incomingMessage.responseId;
  const hasExistingMessage = Boolean(
    responseId &&
      messages.some(
        (message) =>
          message.role === "bot" &&
          String(message.responseId) === String(responseId)
      )
  );

  return hasExistingMessage ? messages : [...messages, incomingMessage];
}

export function classifyQueryResponse(response) {
  const statusCode = response?.status;
  const data = response?.data || {};

  if (statusCode === 202 && data.response_id) {
    const status = String(data.status || "queued").trim().toLowerCase();
    return {
      mode: "accepted",
      responseId: data.response_id,
      sessionId: data.session_id || null,
      sourceDbConfigId: data.source_db_config_id ?? null,
      status,
      terminal: QUERY_JOB_TERMINAL_STATUSES.has(status),
    };
  }

  return { mode: "final" };
}

export function getQueryErrorMessage(error) {
  const statusCode = error?.response?.status;
  const data = error?.response?.data || {};
  const status = String(data.status || "").trim();

  if (status === "faq_stale") {
    return data.error || "Example question changed. Refresh FAQs and try again.";
  }

  if (statusCode === 409 || status === "response_id_conflict") {
    return "This query response was already used by another request. Please retry as a new query.";
  }

  if (statusCode === 503 || status === "enqueue_failed") {
    return "The query queue is temporarily unavailable. Please retry in a moment.";
  }

  return (
    data.error ||
    data.message ||
    error?.message ||
    "An unexpected error occurred. Please try again."
  );
}

const QUERY_STREAM_ERROR_FALLBACKS = {
  quota_exhausted: "Query quota has been exhausted. Please try again after your quota resets.",
  coordination_unavailable:
    "Query coordination is temporarily unavailable. Please retry in a moment.",
  ai_service_unconfigured: "The AI service is not configured. Please contact an administrator.",
};

const QUOTA_ERROR_FIELDS = [
  "quota",
  "used",
  "remaining",
  "quota_scope",
  "user_quota",
  "user_used",
  "user_remaining",
  "tenant_quota",
  "tenant_used",
  "tenant_remaining",
  "effective_remaining",
];

export function getQueryStreamErrorMessage(payload = {}) {
  const status = String(payload.status || "").trim().toLowerCase();
  const backendMessage = String(payload.error || payload.message || "").trim();

  if (backendMessage) {
    return backendMessage;
  }

  return (
    QUERY_STREAM_ERROR_FALLBACKS[status] ||
    "The query could not be completed. Please try again."
  );
}

export function extractQueryQuotaPayload(payload = {}) {
  return QUOTA_ERROR_FIELDS.reduce((quota, key) => {
    if (payload[key] !== undefined) {
      quota[key] = payload[key];
    }
    return quota;
  }, {});
}

export function mergeQueryStreamErrorPayload(message, payload = {}) {
  const status = String(payload.status || "error").trim().toLowerCase();
  const errorMessage = getQueryStreamErrorMessage(payload);
  const quota = extractQueryQuotaPayload(payload);

  return {
    ...message,
    responseId: payload.response_id || message?.responseId || null,
    messageId: payload.message_id || message?.messageId || null,
    status,
    errorStatus: status,
    content: errorMessage,
    sql_response: errorMessage,
    isError: true,
    ssePhase: "error",
    partial: false,
    partialReason: null,
    tableData: null,
    chart: null,
    followUps: [],
    insight: null,
    insightStatus: "failed",
    isInsightEligible: false,
    isDownloadable: false,
    downloadId: null,
    quota: Object.keys(quota).length ? quota : message?.quota,
  };
}

export function mergeQueryTerminalPayload(message, payload = {}) {
  const status = String(payload.status || message?.status || "").trim().toLowerCase();
  const resultId = payload.result_id ?? message?.downloadId ?? null;
  const insight = payload.insight || message?.insight || null;
  const rawInsightStatus = payload.insight_status || message?.insightStatus || "";
  const hasQueryResultEvidence = Boolean(
    payload.message_kind === "query_result" ||
      message?.messageKind === "query_result" ||
      message?.sql ||
      payload.sql ||
      resultId ||
      payload.is_downloadable ||
      message?.isDownloadable ||
      payload.chart_data ||
      message?.chart
  );
  const isInsightEligible = Boolean(
    message?.isInsightEligible ||
      hasQueryResultEvidence ||
      rawInsightStatus ||
      insight
  );
  const insightStatus = insight
    ? "completed"
    : rawInsightStatus || (isInsightEligible ? "not_requested" : message?.insightStatus ?? null);
  const sourceDbId =
    payload.source_db_config_id ??
    payload.source_db_id ??
    message?.sourceDbId ??
    null;

  return {
    ...message,
    status: status || message?.status || null,
    messageId: payload.message_id || message?.messageId || null,
    followUps: Array.isArray(payload.follow_ups) ? payload.follow_ups : message?.followUps || [],
    ssePhase: "completed",
    partial: status === "partial_success" || Boolean(payload.partial ?? message?.partial),
    partialReason: payload.partial_reason || message?.partialReason || null,
    chartStatus: payload.chart_status || message?.chartStatus || null,
    summaryStatus: payload.summary_status || message?.summaryStatus || null,
    isDownloadable: Boolean(resultId || payload.is_downloadable || message?.isDownloadable),
    downloadId: resultId,
    insight,
    insightStatus,
    isInsightEligible,
    sourceDbId,
  };
}
